/**
 * テンプレート管理ページ
 */

import type { EmailTemplate, EmailTemplateId } from "@repo/types";
import { EMAIL_TEMPLATE_METADATA } from "@repo/types";
import { AlertCircle, Eye, FileEdit, Loader2, Mail, RefreshCw, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  generatePreview,
  useTemplate,
  useTemplates,
} from "@/features/templates/hooks/use-templates";

// ============================================
// テンプレート一覧
// ============================================

function TemplateList({
  templates,
  selectedId,
  onSelect,
}: {
  templates: EmailTemplate[];
  selectedId: EmailTemplateId | null;
  onSelect: (id: EmailTemplateId) => void;
}) {
  return (
    <div className="space-y-2">
      {templates.map((template) => {
        const isSelected = template.id === selectedId;
        return (
          <button
            key={template.id}
            type="button"
            onClick={() => onSelect(template.id)}
            className={`w-full text-left rounded-lg border p-3 transition-colors hover:bg-accent ${
              isSelected ? "border-primary bg-accent" : "border-border"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{template.name}</span>
              </div>
              {template.isCustomized && (
                <span className="text-xs text-primary">
                  <FileEdit className="h-3 w-3" />
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-muted-foreground line-clamp-1">
              {template.description}
            </p>
          </button>
        );
      })}
    </div>
  );
}

// ============================================
// テンプレート編集
// ============================================

function TemplateEditor({
  template,
  onSave,
  onReset,
  isSaving,
}: {
  template: EmailTemplate;
  onSave: (data: { subject: string; htmlContent: string; textContent: string }) => Promise<void>;
  onReset: () => Promise<void>;
  isSaving: boolean;
}) {
  const [subject, setSubject] = useState(template.subject);
  const [htmlContent, setHtmlContent] = useState(template.htmlContent);
  const [textContent, setTextContent] = useState(template.textContent);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

  // テンプレートが変わったらフォームをリセット
  useEffect(() => {
    setSubject(template.subject);
    setHtmlContent(template.htmlContent);
    setTextContent(template.textContent);
    setPreviewHtml(null);
  }, [template]);

  // 変更があるかチェック
  const hasChanges =
    subject !== template.subject ||
    htmlContent !== template.htmlContent ||
    textContent !== template.textContent;

  // プレビュー生成
  const handlePreview = async () => {
    setIsGeneratingPreview(true);
    try {
      // サンプル変数を生成
      const metadata = EMAIL_TEMPLATE_METADATA[template.id];
      const sampleVariables: Record<string, string> = {};
      for (const variable of metadata.variables) {
        sampleVariables[variable.name] = variable.example;
      }

      const result = await generatePreview(template.id, sampleVariables);
      if (result) {
        setPreviewHtml(result.html);
      } else {
        toast.error("プレビューの生成に失敗しました");
      }
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // 保存
  const handleSave = async () => {
    await onSave({ subject, htmlContent, textContent });
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{template.name}</h2>
          <p className="text-sm text-muted-foreground">{template.description}</p>
        </div>
        <div className="flex items-center gap-2">
          {template.isCustomized && (
            <Button variant="outline" size="sm" onClick={onReset} disabled={isSaving}>
              <RefreshCw className="mr-2 h-4 w-4" />
              デフォルトに戻す
            </Button>
          )}
          <Button size="sm" onClick={handleSave} disabled={!hasChanges || isSaving}>
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            保存
          </Button>
        </div>
      </div>

      <Separator />

      {/* 変数一覧 */}
      <div className="rounded-lg bg-muted p-4">
        <h3 className="mb-2 text-sm font-medium">利用可能な変数</h3>
        <div className="flex flex-wrap gap-2">
          {EMAIL_TEMPLATE_METADATA[template.id].variables.map((variable) => (
            <code
              key={variable.name}
              className="rounded bg-background px-2 py-1 text-xs"
              title={variable.description}
            >
              {"{{"}
              {variable.name}
              {"}}"}
            </code>
          ))}
        </div>
      </div>

      {/* 編集フォーム */}
      <Tabs defaultValue="html" className="w-full">
        <TabsList>
          <TabsTrigger value="html">HTML</TabsTrigger>
          <TabsTrigger value="text">テキスト</TabsTrigger>
          <TabsTrigger value="preview">プレビュー</TabsTrigger>
        </TabsList>

        <TabsContent value="html" className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="subject">件名</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="メールの件名"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="htmlContent">HTML本文</Label>
            <Textarea
              id="htmlContent"
              value={htmlContent}
              onChange={(e) => setHtmlContent(e.target.value)}
              placeholder="HTMLメール本文"
              className="min-h-[400px] font-mono text-sm"
            />
          </div>
        </TabsContent>

        <TabsContent value="text" className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="textContent">テキスト本文</Label>
            <Textarea
              id="textContent"
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="テキストメール本文"
              className="min-h-[400px] font-mono text-sm"
            />
          </div>
        </TabsContent>

        <TabsContent value="preview" className="space-y-4">
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreview}
              disabled={isGeneratingPreview}
            >
              {isGeneratingPreview ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Eye className="mr-2 h-4 w-4" />
              )}
              プレビュー生成
            </Button>
          </div>
          {previewHtml ? (
            <div className="rounded-lg border bg-white p-4">
              <div
                className="prose prose-sm max-w-none"
                /* biome-ignore lint/security/noDangerouslySetInnerHtml: Preview purpose */
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          ) : (
            <div className="flex h-[400px] items-center justify-center rounded-lg border border-dashed text-muted-foreground">
              「プレビュー生成」をクリックしてプレビューを表示
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* 更新情報 */}
      {template.updatedBy && (
        <div className="text-sm text-muted-foreground">
          最終更新: {new Date(template.updatedAt).toLocaleString("ja-JP")} by {template.updatedBy}
        </div>
      )}
    </div>
  );
}

// ============================================
// メインページ
// ============================================

export function TemplatesPage() {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId?: string }>();
  const selectedId = (templateId as EmailTemplateId) || null;

  const { templates, isLoading: isLoadingList, error: listError, refresh } = useTemplates();
  const { template, isLoading: isLoadingTemplate, update, reset } = useTemplate(selectedId);
  const [isSaving, setIsSaving] = useState(false);

  // テンプレート選択
  const handleSelect = (id: EmailTemplateId) => {
    navigate(`/templates/${id}`);
  };

  // 保存
  const handleSave = async (data: {
    subject: string;
    htmlContent: string;
    textContent: string;
  }) => {
    setIsSaving(true);
    try {
      const success = await update(data);
      if (success) {
        toast.success("テンプレートを保存しました");
        await refresh();
      } else {
        toast.error("保存に失敗しました");
      }
    } finally {
      setIsSaving(false);
    }
  };

  // リセット
  const handleReset = async () => {
    if (!confirm("テンプレートをデフォルトに戻しますか？")) return;

    setIsSaving(true);
    try {
      const success = await reset();
      if (success) {
        toast.success("テンプレートをリセットしました");
        await refresh();
      } else {
        toast.error("リセットに失敗しました");
      }
    } finally {
      setIsSaving(false);
    }
  };

  // ローディング
  if (isLoadingList) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // エラー
  if (listError) {
    return (
      <Card>
        <CardHeader className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
          <CardTitle className="mt-4">エラー</CardTitle>
          <CardDescription>{listError}</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center">
          <Button onClick={refresh}>再読み込み</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
      {/* サイドバー */}
      <div>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              メールテンプレート
            </CardTitle>
            <CardDescription>{templates.length}件のテンプレート</CardDescription>
          </CardHeader>
          <CardContent>
            <TemplateList templates={templates} selectedId={selectedId} onSelect={handleSelect} />
          </CardContent>
        </Card>
      </div>

      {/* メインコンテンツ */}
      <div>
        <Card>
          <CardContent className="pt-6">
            {selectedId && template ? (
              <TemplateEditor
                template={template}
                onSave={handleSave}
                onReset={handleReset}
                isSaving={isSaving}
              />
            ) : isLoadingTemplate ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                <Mail className="h-12 w-12 mb-4" />
                <p>左のリストからテンプレートを選択してください</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
